import type { SoapState } from './soap'

/**
 * Helper functions for note preservation logic in SOAP reducer.
 * These extract complex conditionals to improve readability and maintainability.
 *
 * ## Note Preservation Behavior
 *
 * The note preservation system prevents loss of unsaved audio summaries when navigating
 * between SOAPs. It tracks which SOAP was last mutated via `lastMutationSoapId` and preserves
 * notes when fetching the same SOAP that was just updated.
 *
 * ## Edge Cases
 *
 * **Multiple SOAP Mutations:**
 * - If user mutates notes for SOAP A, navigates away, then mutates notes for SOAP B before
 *   navigating back to SOAP A, only SOAP B's notes will be preserved (lastMutationSoapId
 *   is overwritten). SOAP A's notes will use the fetched values from the backend.
 * - This prevents cross-SOAP contamination where SOAP B's notes would appear in SOAP A.
 *
 * **Performance Considerations:**
 * - Large note strings are stored in Redux state, which is kept in memory. For very large
 *   notes (e.g., >100KB), this could impact memory usage, but typical SOAP notes are much
 *   smaller. The preservation logic uses simple string comparisons and assignments, which
 *   are O(1) operations.
 * - Notes are only preserved when navigating back to the mutated SOAP, so memory is
 *   released when fetching a different SOAP or when `lastMutationSoapId` is cleared.
 *
 * **When `lastMutationSoapId` is Cleared:**
 * - Cleared after successful FETCH_SOAP_SUCCESS/CREATE_SOAP_SUCCESS if fetching the
 *   updated SOAP (prevents stale preservation)
 * - Preserved when closing SOAP (CLOSE_SOAP) to allow preservation when navigating back
 * - Preserved when fetching a different SOAP (to maintain tracking for original SOAP)
 * - Set to null when notes are empty (prevents tracking empty mutations)
 */

/**
 * Determines if notes should be preserved when fetching a SOAP.
 * Preserves notes when fetching the SOAP that was just updated via mutation.
 *
 * **Edge Case:** If user mutates SOAP A, navigates away, mutates SOAP B, then navigates
 * back to SOAP A, this will return false (SOAP A !== lastMutationSoapId), so SOAP A will
 * use fetched notes from backend, preventing SOAP B's notes from appearing in SOAP A.
 */
export const shouldPreserveNotesOnFetch = (
  actionId: string | undefined,
  stateId: string | undefined,
  lastMutationSoapId: string | null,
): boolean => {
  const fetchingUpdatedSoap = actionId === lastMutationSoapId
  const fetchingSameSoap = actionId != null && actionId === stateId
  // Only preserve if fetching the mutated SOAP AND notes match (were stored for this SOAP)
  return fetchingUpdatedSoap && (fetchingSameSoap || !stateId)
}

/**
 * Returns preserved notes object for FETCH_SOAP case.
 * Preserves notes from Redux state if we're fetching the SOAP that was just mutated.
 */
export const getPreservedNotesOnFetch = (
  shouldPreserve: boolean,
  state: SoapState,
): Partial<
  Pick<SoapState, 'medicalNotes' | 'dischargeNotes' | 'lastMutationSoapId'>
> => shouldPreserve
    ? {
        medicalNotes: state.medicalNotes,
        dischargeNotes: state.dischargeNotes,
        lastMutationSoapId: state.lastMutationSoapId,
      }
    : {}

/**
 * Determines if notes should be preserved when fetching/creating SOAP success.
 * Preserves non-empty notes from current state only if fetching/creating the updated SOAP.
 */
export const shouldPreserveNotesOnFetchSuccess = (
  fetchedSoapId: string | undefined,
  lastMutationSoapId: string | null,
): boolean => fetchedSoapId === lastMutationSoapId

/**
 * Returns preserved notes for FETCH_SOAP_SUCCESS/CREATE_SOAP_SUCCESS cases.
 * Preserves non-empty notes from state if fetching the updated SOAP, otherwise uses fetched notes.
 *
 * **Performance:** Uses simple string length checks and assignments. For large notes,
 * the string copy operation is O(n) where n is note length, but this is acceptable
 * since it only happens during SOAP fetch operations (not on every render).
 *
 * **lastMutationSoapId Clearing:** Clears `lastMutationSoapId` when preserving notes
 * (sets to null) to prevent stale preservation on subsequent fetches.
 */
export const getPreservedNotesOnFetchSuccess = (
  shouldPreserve: boolean,
  state: SoapState,
  fetchedMedicalNotes: string,
  fetchedDischargeNotes: string,
): {
  dischargeNotes: string
  lastMutationSoapId: string | null
  medicalNotes: string
} => {
  const preservedMedical =
    shouldPreserve && state.medicalNotes && state.medicalNotes.length > 0
      ? state.medicalNotes
      : fetchedMedicalNotes
  const preservedDischarge =
    shouldPreserve && state.dischargeNotes && state.dischargeNotes.length > 0
      ? state.dischargeNotes
      : fetchedDischargeNotes

  return {
    medicalNotes: preservedMedical,
    dischargeNotes: preservedDischarge,
    lastMutationSoapId: shouldPreserve ? null : state.lastMutationSoapId,
  }
}

/**
 * Determines if notes should be preserved when adding SOAP success.
 * Preserves non-empty notes from current state only if they belong to the new SOAP.
 */
export const shouldPreserveNotesOnAddSuccess = (
  addedSoapId: string | undefined,
  lastMutationSoapId: string | null,
): boolean => addedSoapId === lastMutationSoapId

/**
 * Returns preserved notes for ADD_SOAP_SUCCESS case.
 * Preserves non-empty notes from state if they belong to the new SOAP, otherwise uses added notes.
 */
export const getPreservedNotesOnAddSuccess = (
  shouldPreserve: boolean,
  state: SoapState,
  addedMedicalNotes: string,
  addedDischargeNotes: string,
): {
  dischargeNotes: string
  medicalNotes: string
} => {
  const preservedMedicalNotes =
    shouldPreserve && state.medicalNotes && state.medicalNotes.length > 0
      ? state.medicalNotes
      : addedMedicalNotes
  const preservedDischargeNotes =
    shouldPreserve && state.dischargeNotes && state.dischargeNotes.length > 0
      ? state.dischargeNotes
      : addedDischargeNotes

  return {
    medicalNotes: preservedMedicalNotes,
    dischargeNotes: preservedDischargeNotes,
  }
}

/**
 * Gets the SOAP ID for note operations using fallback chain.
 * Uses soapId from action if provided, otherwise falls back to lastMutationSoapId,
 * then current SOAP ID, or null.
 */
export const getNotesSoapId = (
  actionSoapId: string | null | undefined,
  lastMutationSoapId: string | null,
  currentSoapId: string | undefined,
): string | null => actionSoapId ?? lastMutationSoapId ?? currentSoapId ?? null

/**
 * Determines if notes should be stored to Redux state.
 * Updates Redux state if:
 * 1. Mutation was for the currently loaded SOAP (state.id matches), OR
 * 2. No SOAP is currently loaded (state.id is null) - allows preserving notes when off SOAP page
 */
export const shouldStoreNotes = (
  soapId: string | null,
  currentSoapId: string | undefined,
): boolean => {
  const isCurrentSoap = soapId && currentSoapId && soapId === currentSoapId
  const isNoSoapLoaded = !currentSoapId
  return isCurrentSoap || isNoSoapLoaded
}

/**
 * Returns updated state for SET_MEDICAL_NOTES/SET_DISCHARGE_NOTES cases.
 * Only updates notes and lastMutationSoapId if notes should be stored.
 */
export const getUpdatedNotesState = (
  notesValue: string,
  soapId: string | null,
  notesWereStored: boolean,
  currentLastMutationSoapId: string | null,
  fieldName: 'medicalNotes' | 'dischargeNotes',
): Partial<
  Pick<SoapState, 'medicalNotes' | 'dischargeNotes' | 'lastMutationSoapId'>
> => ({
    ...(notesWereStored ? { [fieldName]: notesValue } : {}),
    lastMutationSoapId: notesWereStored ? soapId : currentLastMutationSoapId,
  })

/**
 * Returns preserved state for CLOSE_SOAP case.
 * Preserves lastMutationSoapId and notes when closing SOAP to allow notes preservation
 * when user navigates back after a mutation completed on another page.
 */
export const getPreservedStateOnClose = (
  state: SoapState,
): Partial<
  Pick<SoapState, 'medicalNotes' | 'dischargeNotes' | 'lastMutationSoapId'>
> => {
  // Always preserve lastMutationSoapId and notes if they exist
  // This allows mutations that happen AFTER CLOSE_SOAP to still be tracked
  // Also preserve notes even if lastMutationSoapId is null but notes exist (defensive)
  const hasMutationData =
    state.lastMutationSoapId ||
    (state.medicalNotes && state.medicalNotes.length > 0) ||
    (state.dischargeNotes && state.dischargeNotes.length > 0)

  return hasMutationData
    ? {
        lastMutationSoapId: state.lastMutationSoapId,
        medicalNotes: state.medicalNotes,
        dischargeNotes: state.dischargeNotes,
      }
    : {}
}
